import { useMemo, useState } from "react";
import { Divider, Input, Spinner, Tab, TabList, Text, Title3 } from "@fluentui/react-components";
import { Search20Regular } from "@fluentui/react-icons";
import { useTickets, useTicketTimeline } from "../../api/hooks";
import { TicketTable, isClosed } from "../../components/TicketTable";
import { TimelineView } from "./AdminReportsPage";
import type { Priority, Ticket } from "../../types/models";

type TabKey = "all" | "open" | "closed";

const PRIORITY_FILTER: Array<Priority | "all"> = ["all", "Critical", "High", "Medium", "Low"];
const PRIORITY_COLOR: Record<string, string> = {
  Critical: "#D13438",
  High: "#F7630C",
  Medium: "#0078D4",
  Low: "#8A8886",
};

export function AdminTicketsPage() {
  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState<Priority | "all">("all");
  const [selected, setSelected] = useState<Ticket | undefined>(undefined);

  const tickets = useTickets({ sort: "number_desc", pageSize: 500 });
  const all = tickets.data?.items ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((t) => {
      if (tab === "open" && isClosed(t.status)) return false;
      if (tab === "closed" && !isClosed(t.status)) return false;
      if (priority !== "all" && t.priority !== priority) return false;
      if (!q) return true;
      return (
        t.title.toLowerCase().includes(q) ||
        `it-${t.ticketNumber}`.includes(q) ||
        (t.requester?.displayName ?? "").toLowerCase().includes(q) ||
        (t.assignee?.displayName ?? "").toLowerCase().includes(q)
      );
    });
  }, [all, tab, search, priority]);

  const openCount = all.filter((t) => !isClosed(t.status)).length;
  const closedCount = all.length - openCount;
  const criticalOpen = all.filter((t) => t.priority === "Critical" && !isClosed(t.status)).length;
  const breachedCount = all.filter(
    (t) => !isClosed(t.status) && t.slaDueAt && new Date(t.slaDueAt) < new Date()
  ).length;

  const timeline = useTicketTimeline(selected?.id);

  return (
    <div>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <Title3>All Tickets</Title3>
          <Text style={{ display: "block", color: "#605E5C", marginTop: 2, fontSize: 13 }}>
            Every ticket across all queues. Click a title to view the detailed report inline.
          </Text>
        </div>
        {/* Alert badges */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {criticalOpen > 0 && (
            <AlertPill count={criticalOpen} label="Critical open" color="#D13438" />
          )}
          {breachedCount > 0 && (
            <AlertPill count={breachedCount} label="SLA breached" color="#F7630C" />
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <TabList
          selectedValue={tab}
          onTabSelect={(_, d) => { setTab(d.value as TabKey); setSelected(undefined); }}
        >
          <Tab value="all">All ({all.length})</Tab>
          <Tab value="open">Open ({openCount})</Tab>
          <Tab value="closed">Closed ({closedCount})</Tab>
        </TabList>
        <Input
          style={{ minWidth: 280 }}
          contentBefore={<Search20Regular />}
          placeholder="Search title, #, requester, assignee…"
          value={search}
          onChange={(_, d) => setSearch(d.value)}
        />
      </div>

      {/* Priority filter pills */}
      <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Text size={200} style={{ color: "#8A8886", marginRight: 4 }}>Priority:</Text>
        {PRIORITY_FILTER.map((p) => {
          const active = priority === p;
          const color = p === "all" ? "#605E5C" : PRIORITY_COLOR[p];
          const countForPill = p === "all" ? filtered.length : filtered.filter((t) => t.priority === p).length;
          return (
            <button
              key={p}
              onClick={() => setPriority(p)}
              style={{
                padding: "3px 12px",
                borderRadius: 999,
                border: `1.5px solid ${active ? color : "#EDEBE9"}`,
                background: active ? `${color}15` : "#F8F8F8",
                color: active ? color : "#605E5C",
                fontWeight: active ? 700 : 400,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.12s",
              }}
            >
              {p === "all" ? "All" : p}
              {" "}
              <span style={{ opacity: 0.7, fontWeight: 400 }}>({countForPill})</span>
            </button>
          );
        })}
      </div>

      {/* Ticket table */}
      <div style={{ marginTop: 14 }}>
        {tickets.isLoading ? (
          <div style={{ padding: "40px 0", textAlign: "center" }}>
            <Spinner label="Loading tickets…" />
          </div>
        ) : (
          <>
            <TicketTable
              tickets={filtered}
              showRequester
              editable
              onSelect={setSelected}
              selectedId={selected?.id}
            />
            {tickets.data && tickets.data.total > all.length && (
              <p style={{ marginTop: 10, color: "#8A8886", fontSize: 12 }}>
                Showing {all.length} of {tickets.data.total} tickets.
              </p>
            )}
          </>
        )}
      </div>

      {/* Inline ticket detail */}
      {selected && (
        <>
          <Divider style={{ margin: "24px 0 16px" }} />
          <div style={{
            background: "rgba(98,100,167,0.03)",
            border: "1px solid rgba(98,100,167,0.18)",
            borderRadius: 10,
            padding: "18px 22px",
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14, gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, color: "#8A8886", marginBottom: 2 }}>Ticket detail</div>
                <strong style={{ fontSize: 16 }}>IT-{selected.ticketNumber}: {selected.title}</strong>
              </div>
              <button
                onClick={() => setSelected(undefined)}
                title="Close"
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: "#8A8886",
                  fontSize: 20,
                  padding: "0 6px",
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
            {timeline.isFetching && <Spinner size="small" label="Loading timeline…" />}
            {timeline.data && <TimelineView data={timeline.data} />}
          </div>
        </>
      )}
    </div>
  );
}

function AlertPill({ count, label, color }: { count: number; label: string; color: string }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 12px",
      borderRadius: 6,
      background: `${color}12`,
      border: `1px solid ${color}35`,
    }}>
      <span style={{ fontSize: 16, fontWeight: 700, color }}>{count}</span>
      <span style={{ fontSize: 12, color: "#605E5C" }}>{label}</span>
    </div>
  );
}
